import React, { useEffect, useState } from "react";
import { View, StyleSheet, ScrollView, Linking } from "react-native";

import CustomText from "../../../common/general/CustomText/CustomText";
import ProjectGradingSection from "./ProjectGradingSection";
import { useGetProjectSubmissionQuery } from "../../../../graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";
import { formatSubmittedDate, formatSubmittedTime } from "../../../../utils/date";

const getStatusColor = (status: string) => {
  switch (status) {
    case "COMPLETED":
    case "Completed":
      return { bg: "#d1fae5", text: "#065f46" };
    case "NEEDS_REVIEW":
    case "Needs Review":
      return { bg: "#fecaca", text: "#dc2626" };
    case "APPROVED":
      return { bg: "#dbeafe", text: "#1e40af" };
    case "REJECTED":
      return { bg: "#fee2e2", text: "#991b1b" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280" };
  }
};

const ProjectGradingPanel: React.FC = () => {
  const { selectedSubmissionData, updatedProjectPayload, setUpdatedProjectPayload } = useGradingCenterSubmissions();

  const { data: projectSubmissionDetails } = useGetProjectSubmissionQuery({
    variables: { id: selectedSubmissionData?.id || "" },
    skip: !selectedSubmissionData?.id || selectedSubmissionData?.submissionType !== "Project",
  });

  const projectSubmission = projectSubmissionDetails?.getProjectSubmission?.data;
  const project = projectSubmission?.project;

  useEffect(() => {
    if (selectedSubmissionData?.id) {
      const grade = projectSubmission?.grade ?? selectedSubmissionData?.grade ?? null;
      const feedback = projectSubmission?.feedback ?? "";
      setUpdatedProjectPayload({ grade, feedback });
    }
  }, [selectedSubmissionData?.id, projectSubmission?.grade, projectSubmission?.feedback, setUpdatedProjectPayload]);

  const grade = updatedProjectPayload?.grade ?? null;
  const feedback = updatedProjectPayload?.feedback ?? "";

  const handleGradeChange = (newGrade: number | null) => {
    setUpdatedProjectPayload({
      ...(updatedProjectPayload || { grade: null, feedback: "" }),
      grade: newGrade,
    });
  };

  const handleFeedbackChange = (newFeedback: string) => {
    setUpdatedProjectPayload({
      ...(updatedProjectPayload || { grade: null, feedback: "" }),
      feedback: newFeedback,
    });
  };

  const statusColors = getStatusColor(selectedSubmissionData?.status || "");

  return (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false} tabIndex={0}>
      {/* Project Info Header */}
      <View style={styles.projectInfoHeader}>
        <View style={styles.projectBasicInfo}>
          <CustomText
            text={project?.name}
            size="l"
            weight="bold"
            style={styles.projectTitle}
            useTranslationText={false}
          />
          <View style={styles.projectMeta}>
            {selectedSubmissionData?.courseName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.courseName} size="xs" useTranslationText={false} />
              </View>
            )}
            {selectedSubmissionData?.moduleName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.moduleName} size="xs" useTranslationText={false} />
              </View>
            )}
            {project?.dueDate && (
              <View style={styles.metaTag}>
                <CustomText
                  text={`Due: ${new Date(project.dueDate).toLocaleDateString()}`}
                  size="xs"
                  useTranslationText={false}
                />
              </View>
            )}
          </View>
        </View>
        <View style={styles.projectStatusInfo}>
          <View style={styles.submissionStatus}>
            <View style={[styles.badge, { backgroundColor: statusColors.bg }]}>
              <CustomText
                text={selectedSubmissionData?.status}
                size="xs"
                style={[styles.statusText, { color: statusColors.text }]}
              />
            </View>
          </View>
        </View>
      </View>

      {/* Student Info */}
      <View style={styles.studentSubmissionInfo}>
        <View style={styles.studentDetails}>
          <View style={styles.studentAvatar}>
            <CustomText
              text={selectedSubmissionData?.student?.initials}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.studentInfo}>
            <CustomText
              text={selectedSubmissionData?.student?.name}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
            <CustomText
              text={selectedSubmissionData?.student?.email}
              size="xs"
              style={styles.email}
              useTranslationText={false}
            />
          </View>
        </View>
        <View style={styles.submissionDetails}>
          <CustomText
            text="submitted_on"
            size="xs"
            interpolation={{ value: formatSubmittedDate(selectedSubmissionData?.submittedOn) }}
          />
          <CustomText
            text="submitted_time"
            size="xs"
            interpolation={{ value: formatSubmittedTime(selectedSubmissionData?.submittedTime) }}
          />
        </View>
      </View>

      {/* Student Submission */}
      <View style={styles.studentSubmissionSection}>
        <CustomText text="Student Submission" weight="bold" style={styles.sectionTitle} />
        {projectSubmission?.submissionLink && (
          <View style={styles.submissionLinkContainer}>
            <CustomText
              text="Submission Link:"
              size="s"
              weight="bold"
              style={styles.linkLabel}
              useTranslationText={false}
            />
            <CustomText
              text={projectSubmission.submissionLink}
              size="s"
              style={styles.link}
              onPress={() => Linking.openURL(projectSubmission.submissionLink!)}
              useTranslationText={false}
            />
          </View>
        )}
        {projectSubmission?.submissionNotes && (
          <View style={styles.submissionNotesContainer}>
            <CustomText
              text="Submission Notes:"
              size="s"
              weight="bold"
              style={styles.notesLabel}
              useTranslationText={false}
            />
            <CustomText
              text={projectSubmission.submissionNotes}
              size="s"
              style={styles.notes}
              useTranslationText={false}
            />
          </View>
        )}
        {projectSubmission?.files && projectSubmission.files.length > 0 && (
          <View style={styles.filesContainer}>
            {projectSubmission.files.map((file) => (
              <FileSubmissionItem key={file.id} file={file} />
            ))}
          </View>
        )}
      </View>

      {/* Grading Section */}
      <ProjectGradingSection
        grade={grade}
        feedback={feedback}
        onGradeChange={handleGradeChange}
        onFeedbackChange={handleFeedbackChange}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 24,
  },
  projectInfoHeader: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
  },
  projectBasicInfo: {
    flex: 1,
  },
  projectTitle: {
    color: "#111827",
    marginBottom: 8,
  },
  projectMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metaTag: {
    backgroundColor: "#F2F4F7",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  projectStatusInfo: {
    alignItems: "flex-end",
  },
  submissionStatus: {
    alignItems: "flex-end",
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  statusText: {
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  studentSubmissionInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  email: {
    color: "#6B7280",
    marginTop: 2,
  },
  submissionDetails: {
    alignItems: "flex-end",
  },
  studentSubmissionSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 8,
  },
  submissionLinkContainer: {
    marginBottom: 16,
  },
  linkLabel: {
    color: "#374151",
    marginBottom: 4,
  },
  link: {
    color: "#3B82F6",
    textDecorationLine: "underline",
  },
  submissionNotesContainer: {
    marginBottom: 16,
  },
  notesLabel: {
    color: "#374151",
    marginBottom: 4,
  },
  notes: {
    color: "#6B7280",
  },
  filesContainer: {
    gap: 12,
  },
});

export default ProjectGradingPanel;
